import secureLocalStorage from "react-secure-storage";
import * as StorageConstants from "../../Models/SecureStorageConstants";
import { CustomerService } from "../../Services/customerService";

/**
 * Per-customer logo cache in localStorage (key "customerLogos", uuid → data URL).
 * The sidebar reads it synchronously for its first paint; the sign-in flow warms it
 * before navigating into the app so a customer logo never flashes as the Dashly one.
 */

const STORAGE_KEY = "customerLogos";
/** Longest the sign-in flow waits for the logo before entering the app anyway. */
export const LOGO_WARMUP_TIMEOUT_MS = 1500;

const readStore = (): Record<string, string> => {
	try {
		return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
	} catch {
		return {};
	}
};

export const readCachedCustomerLogo = (customerUuid?: string | null): string | null => {
	if (!customerUuid) {
		return null;
	}
	return readStore()[customerUuid] || null;
};

export const writeCachedCustomerLogo = (customerUuid: string, logo: string): void => {
	try {
		const store = readStore();
		store[customerUuid] = logo;
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
	} catch {
		// localStorage may be unavailable
	}
};

/**
 * Customer uuid of the signed-in user, from the stored user object. The sign-in
 * response (SecurityUserResponse) carries it as `customerUuid`; `customer.uuid` is
 * accepted too for older stored shapes.
 */
export const storedUserCustomerUuid = (): string | null => {
	try {
		const userStr = secureLocalStorage.getItem(StorageConstants.USER);
		if (!userStr || typeof userStr !== "string") {
			return null;
		}
		const user = JSON.parse(userStr);
		return user?.customerUuid ?? user?.customer?.uuid ?? null;
	} catch {
		return null;
	}
};

/**
 * Makes sure the logo for this customer is cached. Resolves to the logo (or null
 * when the customer has none / the request failed); never throws.
 */
export const warmCustomerLogoCache = async (customerUuid?: string | null): Promise<string | null> => {
	if (!customerUuid) {
		return null;
	}
	const cached = readCachedCustomerLogo(customerUuid);
	if (cached) {
		return cached;
	}
	try {
		const response = await new CustomerService().getCustomerLogo(customerUuid);
		const logo = response?.data?.data?.logo;
		if (logo) {
			writeCachedCustomerLogo(customerUuid, logo);
			return logo;
		}
	} catch {
		// keep the default logo
	}
	return null;
};

/** `warmCustomerLogoCache` with an upper bound on how long a caller waits for it. */
export const warmCustomerLogoCacheBounded = (customerUuid?: string | null): Promise<string | null> => {
	const timeout = new Promise<null>((resolve) => {
		setTimeout(() => resolve(null), LOGO_WARMUP_TIMEOUT_MS);
	});
	return Promise.race([warmCustomerLogoCache(customerUuid), timeout]);
};
